import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StageScreen, StagePopup, popupHasContent } from './components/StageShell';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

function shuffledBoard(total) {
  const order = [...Array(total).keys()];
  let a = order;
  do {
    a = [...order];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
  } while (total > 1 && a.every((p, i) => p === i));
  return a;
}

export default function Stage6Jigsaw({ stage, pagesBg, onComplete }) {
  const grid = Math.min(4, Math.max(2, Number(stage?.grid_size) || 3));
  const total = grid * grid;
  const imgUrl = s3PublicUrl(stage?.puzzle_image);

  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);
  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const [board, setBoard] = useState(() => shuffledBoard(total));
  const [draggedCell, setDraggedCell] = useState(null);

  // Touch drag state
  const [touchDragCell, setTouchDragCell] = useState(null);
  const ghostRef = useRef(null);
  const touchDragCellRef = useRef(null);
  const hoverCellRef = useRef(null);
  // Keep a stable ref to board + pieceStyle so the touch effect closure can
  // read the current board without being listed as a dependency.
  const boardRef = useRef(board);
  useEffect(() => { boardRef.current = board; }, [board]);

  useEffect(() => {
    setBoard(shuffledBoard(total));
    setDraggedCell(null);
  }, [total]);

  const solved = useMemo(
    () => board.length === total && board.every((p, i) => p === i),
    [board, total]
  );

  useEffect(() => {
    if (solved) {
      const t = setTimeout(() => {
        if (endHasContent) setPhase('end');
        else onComplete({ solved: true });
      }, 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [solved, endHasContent, onComplete]);

  const pct = 100 * grid;
  const pieceStyle = useCallback((pieceIdx) => {
    const row = Math.floor(pieceIdx / grid);
    const col = pieceIdx % grid;
    return imgUrl
      ? {
          backgroundImage: `url(${imgUrl})`,
          backgroundSize: `${pct}% ${pct}%`,
          backgroundPosition: `${(col / (grid - 1)) * 100}% ${(row / (grid - 1)) * 100}%`,
        }
      : { background: 'rgba(255,255,255,0.15)' };
  }, [grid, imgUrl, pct]);

  const swapCells = useCallback((fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    setBoard((b) => {
      // A piece already sitting in its correct cell must never be disturbed —
      // guarding here (rather than at each call site) covers every path
      // (mouse drag, touch drag) that can trigger a swap.
      if (b[fromIdx] === fromIdx || b[toIdx] === toIdx) return b;
      const next = [...b];
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      return next;
    });
  }, []);

  // ── Touch drag ──────────────────────────────────────────────────────────────

  const onPieceTouchStart = useCallback((cellIdx, e) => {
    if (solved) return;
    // A piece already on its correct cell is locked — don't let it be picked up.
    if (boardRef.current[cellIdx] === cellIdx) return;
    e.preventDefault();

    touchDragCellRef.current = cellIdx;
    setTouchDragCell(cellIdx);
    setDraggedCell(cellIdx);

    const touch = e.touches[0];
    const pieceEl = e.currentTarget;
    const rect = pieceEl.getBoundingClientRect();
    const size = rect.width;

    const pieceIdx = boardRef.current[cellIdx];
    const style = (() => {
      const row = Math.floor(pieceIdx / grid);
      const col = pieceIdx % grid;
      return imgUrl
        ? {
            backgroundImage: `url(${imgUrl})`,
            backgroundSize: `${pct}% ${pct}%`,
            backgroundPosition: `${(col / (grid - 1)) * 100}% ${(row / (grid - 1)) * 100}%`,
          }
        : null;
    })();

    const ghost = document.createElement('div');
    ghost.className = 'fi-jigsaw-ghost';
    ghost.style.cssText = `
      position:fixed;pointer-events:none;z-index:9999;
      width:${size}px;height:${size}px;
      transform:translate(-50%,-50%);
      left:${touch.clientX}px;top:${touch.clientY}px;
      border-radius:4px;
      box-shadow:0 8px 28px rgba(0,0,0,0.55);
      opacity:0.92;
      background-repeat:no-repeat;
      ${style ? `background-image:${style.backgroundImage};background-size:${style.backgroundSize};background-position:${style.backgroundPosition};` : 'background:rgba(255,255,255,0.2);'}
    `;

    document.body.appendChild(ghost);
    ghostRef.current = ghost;
  }, [solved, grid, imgUrl, pct]);

  useEffect(() => {
    if (touchDragCell == null) return;

    const clearHover = () => {
      if (hoverCellRef.current != null) {
        const el = document.querySelector(`[data-cell-idx="${hoverCellRef.current}"]`);
        el?.classList.remove('fi-jigsaw-cell--hover');
        hoverCellRef.current = null;
      }
    };

    const onMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];

      if (ghostRef.current) {
        ghostRef.current.style.left = touch.clientX + 'px';
        ghostRef.current.style.top = touch.clientY + 'px';
        ghostRef.current.style.visibility = 'hidden';
      }
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghostRef.current) ghostRef.current.style.visibility = '';

      const cellEl = el?.closest('[data-cell-idx]');
      const newIdx = cellEl ? Number(cellEl.dataset.cellIdx) : null;
      // A cell already holding its correct piece isn't a valid drop target —
      // don't highlight/scale it as if a swap would happen there.
      const isLockedCell = newIdx != null && boardRef.current[newIdx] === newIdx;
      const validIdx = (newIdx != null && !Number.isNaN(newIdx) && newIdx !== touchDragCellRef.current && !isLockedCell)
        ? newIdx : null;

      if (validIdx !== hoverCellRef.current) {
        clearHover();
        if (validIdx != null && cellEl) {
          cellEl.classList.add('fi-jigsaw-cell--hover');
          hoverCellRef.current = validIdx;
        }
      }
    };

    const onEnd = (e) => {
      const touch = e.changedTouches[0];
      const fromIdx = touchDragCellRef.current;

      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHover();

      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const cellEl = el?.closest('[data-cell-idx]');
      const toIdx = cellEl ? Number(cellEl.dataset.cellIdx) : null;

      if (toIdx != null && !Number.isNaN(toIdx) && fromIdx != null && toIdx !== fromIdx) {
        swapCells(fromIdx, toIdx);
      }

      touchDragCellRef.current = null;
      setTouchDragCell(null);
      setDraggedCell(null);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);

    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHover();
    };
  }, [touchDragCell, swapCells]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_6" stage={stage}>
        <StagePopup image={stage?.start_popup_image} text={stage?.start_popup_text} onNext={() => setPhase('game')} />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_6" stage={stage}>
        <StagePopup image={stage?.end_popup_image} text={stage?.end_popup_text} onNext={() => onComplete({ solved: true })} />
      </StageScreen>
    );
  }

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_6" stage={stage}>
      <div className="fi-stage-root fi-jigsaw-root">
        <p className="fi-jigsaw-title">{stage?.title || 'Solve the Jigsaw Puzzle'}</p>

        <div className="fi-jigsaw-layout">
          <div
            className="fi-jigsaw-board fi-jigsaw-board--shuffle"
            style={{ gridTemplateColumns: `repeat(${grid}, 1fr)` }}
          >
            {board.map((piece, cellIdx) => (
              <div
                key={cellIdx}
                data-cell-idx={cellIdx}
                className={`fi-jigsaw-cell ${draggedCell === cellIdx ? 'fi-jigsaw-cell--dragging' : ''} ${
                  piece === cellIdx ? 'fi-jigsaw-cell--correct' : ''
                }`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIdx = Number(e.dataTransfer.getData('text/plain'));
                  swapCells(Number.isNaN(fromIdx) ? draggedCell : fromIdx, cellIdx);
                  setDraggedCell(null);
                }}
              >
                <div
                  className="fi-jigsaw-piece"
                  style={pieceStyle(piece)}
                  draggable={!solved && piece !== cellIdx}
                  onDragStart={(e) => {
                    setDraggedCell(cellIdx);
                    e.dataTransfer.setData('text/plain', String(cellIdx));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => setDraggedCell(null)}
                  onTouchStart={(e) => onPieceTouchStart(cellIdx, e)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </StageScreen>
  );
}
